import React from 'react'
import ReactSlider from 'react-slider'
import MediaQuery from 'react-responsive'
import Modal from 'react-modal'

const MIN_PRICE = 0
const MAX_PRICE = 15000

import ScrollableFilters from '../common/components/scrollable_filters'
import { Filters, AvailableFilters } from '../common/models/properties'
import { Currency, currencySymbol } from '../common/utils/currency'

const ModalStyles = {
  content : {
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    'overflow-y': 'scroll',
    position: 'relative',
    'padding-top': '50px'
  }
};

interface SidebarProps {
  currency?: Currency
  initialFilters: Filters
  filtersUpdated: (filters: Filters) => void
}

interface SidebarState extends Filters {
  showRefineModal: boolean
  filters: AvailableFilters
}

export default class Sidebar extends React.Component<SidebarProps, SidebarState> {
  static get defaultProps() {
    return {
      initialFilters: {},
      filtersUpdated: function() {}
    }
  }

  constructor(props: SidebarProps) {
    super(props)

    this.showRefineModal = this.showRefineModal.bind(this)
    this.closeRefineModal = this.closeRefineModal.bind(this)

    this.updatePropertyType = this.updatePropertyType.bind(this)
    this.updateInterests = this.updateInterests.bind(this)
    this.updateBedrooms = this.updateBedrooms.bind(this)
    this.updateSleeps = this.updateSleeps.bind(this)
    this.updateRegions = this.updateRegions.bind(this)
    this.updateFacilities = this.updateFacilities.bind(this)
    this.clearFilters = this.clearFilters.bind(this)
    this.updatePrice = this.updatePrice.bind(this)
    this.updateMinPrice = this.updateMinPrice.bind(this)
    this.updateMaxPrice = this.updateMaxPrice.bind(this)
    this.afterUpdatePrices = this.afterUpdatePrices.bind(this)

    this.state = Object.assign({}, {
      filters: {
        property_types: [],
        experiences: [],
        bedrooms: [],
        sleeps: [],
        regions: [],
        facilities: []
      },
      type: '',
      interest_ids: [],
      bedrooms: '',
      sleeps: '',
      price: { min: MIN_PRICE, max: MAX_PRICE },
      facilities: [],
      region_id: [],

      showRefineModal: false
    }, props.initialFilters)
  }

  showRefineModal() {
    this.setState({
      showRefineModal: true
    })
  }

  closeRefineModal() {
    this.setState({
      showRefineModal: false
    })
  }

  componentDidMount() {
    const self = this

    fetch(`/api/v1/properties/filters`, {
      method: "GET",
      credentials: 'same-origin'
    })
    .then(resp => {
      if (resp.status >= 200 && resp.status < 300) {
        return resp.json()
      }
    })
    .then(json => {
      if (json) {
        self.setState({
          filters: json
        })
      }
    })
  }

  groupRegions(regions) {
    var tree = regions.filter(r => { return !r.parent_id })

    for (var i = 0; i < tree.length; i++) {
      tree[i].children = regions.filter(r => { return r.parent_id == tree[i].id })
      for (var j = 0; j < tree[i].children.length; j++) {
        tree[i].children[j].children = regions.filter(r => { return r.parent_id == tree[i].children[j].id })
      }
    }

    return tree
  }

  groupExperiences(experiences) {
    var tree = experiences.filter(e => { return e.parent_ids.length == 0 })

    for (var i = 0; i < tree.length; i++) {
      tree[i].children = experiences.filter(r => { return r.parent_ids.includes(tree[i].id) })
    }

    return tree
  }

  // All selected regions must belong to the same parent, validate this or return only the new region
  canSelectRegion(selected) {
    if (selected.length < 1) {
      return []
    }

    const newlySelected = this.state.filters.regions.find(r => { return r.id == selected[selected.length - 1] })

    if (newlySelected) {
      var newlySelectedParent = this.state.filters.regions.find(r => { return r.id == newlySelected.parent_id })

      if (newlySelectedParent) {
        return selected.filter(s => {
          return s != newlySelectedParent.id && s != newlySelectedParent.parent_id
        })
      }
    }

    return selected
  }

  emitFilters() {
    const { type, interest_ids, bedrooms, sleeps, price, region_id, facilities } = this.state

    this.props.filtersUpdated({
      type: type,
      interest_ids: interest_ids,
      bedrooms: bedrooms,
      sleeps: sleeps,
      price: (price.min != MIN_PRICE || price.max != MAX_PRICE) ? price : { min: MIN_PRICE, max: MAX_PRICE },
      region_id: region_id,
      facilities: facilities
    })
  }

  clearFilters() {
    const self = this

    this.setState({
      region_id: [],
      facilities: []
    }, () => {
      self.emitFilters()
    })
  }

  updatePropertyType(event) {
    const self = this
    this.setState({
      type: event.target.value
    }, () => {
      self.emitFilters()
    })
  }

  updateInterests(filtered) {
    const self = this
    this.setState({
      interest_ids: filtered
    }, () => {
      self.emitFilters()
    })
  }

  updateBedrooms(event) {
    const self = this
    this.setState({
      bedrooms: event.target.value
    }, () => {
      self.emitFilters()
    })
  }

  updateSleeps(event) {
    const self = this
    this.setState({
      sleeps: event.target.value
    }, () => {
      self.emitFilters()
    })
  }
  
  updatePrice(value) {
    this.setState({
      price: { min: value[0], max: value[1] }
    })
  }

  updateMinPrice(event) {
    const self = this
    this.setState({
      price: { min: parseInt(event.target.value), max: this.state.price.max }
    }, () => {
      self.afterUpdatePrices()
    })
  }

  updateMaxPrice(event) {
    const self = this
    this.setState({
      price: { min: this.state.price.min, max: parseInt(event.target.value) }
    }, () => {
      self.afterUpdatePrices()
    })
  }

  afterUpdatePrices() {
    this.emitFilters()
  }

  updateRegions(filtered) {
    const self = this
    this.setState({
      region_id: self.canSelectRegion(filtered)
    }, () => {
      self.emitFilters()
    })
  }

  updateFacilities(filtered) {
    const self = this
    this.setState({
      facilities: filtered
    }, () => {
      self.emitFilters()
    })
  }

  renderOptions() {
    const currency = this.props.currency || Currency.EUR
    const { price } = this.state

    const price_step = 100
    let price_steps = []
    for (var i = 0; i <= (MAX_PRICE / price_step); i++) { price_steps.push(i * price_step) }

    const property_type_options = this.state.filters.property_types.map(pt => {
      return <option key={pt.slug} value={pt.slug}>{ pt.plural }</option>
    })

    const interest_filters = this.groupExperiences(this.state.filters.experiences.map(r => {
      r.slug = String(r.id)
      return r
    }))

    const bedrooms_options = this.state.filters.bedrooms.map(b => {
      return <option key={b} value={b}>{ `${b}+ Bedrooms` }</option>
    })

    const sleeps_options = this.state.filters.sleeps.map(s => {
      return <option key={s} value={s}>{ `${s}+ Sleeps` }</option>
    })

    const region_filters = this.groupRegions(this.state.filters.regions.map(r => {
      // Convert region slug to id
      r.slug = String(r.id)
      return r
    }))

    return (
      <div className="destinations-sidebar">
        <h2>Search</h2>

        <h5>Experiences</h5>
        <ScrollableFilters filters={interest_filters} selected={this.state.interest_ids} onFiltersChanged={this.updateInterests} />

        <hr />

        <select onChange={this.updatePropertyType} value={this.state.type}>
          <option value="">Property Type</option>
          { property_type_options }
        </select>

        <select onChange={this.updateBedrooms} value={this.state.bedrooms}>
          <option value="">No. of Bedrooms</option>
          { bedrooms_options }
        </select>

        <select onChange={this.updateSleeps} value={this.state.sleeps}>
          <option value="">Sleeps</option>
          { sleeps_options }
        </select>

        <MediaQuery minWidth={992}>
          <div className="destinations-price">
            <label>Price per night: { currencySymbol(currency) }{price.min} - { currencySymbol(currency) }{price.max}</label>
            <ReactSlider className="react-slider" min={0} max={MAX_PRICE} step={price_step} minDistance={price_step} value={[price.min, price.max]} onChange={this.updatePrice} onAfterChange={this.afterUpdatePrices} />
            <div className="slider-ranges">
              <span>{ currencySymbol(currency) } 0</span>
              <span>{ currencySymbol(currency) } 15,000</span>
            </div>
          </div>
        </MediaQuery>

        <MediaQuery maxWidth={991}>
          <select onChange={this.updateMinPrice} value={price.min}>
            { price_steps.map((s) => (
              <option key={`min-${s}`} value={s}>{ `From €${s}`}</option>
            ))}
          </select>

          <select onChange={this.updateMaxPrice} value={price.max}>
            { price_steps.map((s) => (
              <option key={`max-${s}`} value={s}>{ `Max €${s}`}</option>
            ))}
          </select>
        </MediaQuery>

        <h2 className="mg-t">
          Filters
          <span className="clear-filters" onClick={this.clearFilters}>Clear All</span>
        </h2>

        <h5>Location</h5>
        <ScrollableFilters filters={region_filters} selected={this.state.region_id} onFiltersChanged={this.updateRegions} />

        <hr/>
        <h5>Facilities</h5>
        <ScrollableFilters filters={this.state.filters.facilities} selected={this.state.facilities} onFiltersChanged={this.updateFacilities} />
      </div>
    )
  }

  render() {
    return (
      <div>
        <MediaQuery minDeviceWidth={768}>
          { this.renderOptions() }
        </MediaQuery>
        <MediaQuery maxDeviceWidth={767}>
          <button className="button hollow primary large expanded icon-right" onClick={this.showRefineModal}>
            Refine
            <i className="icon fa fa-filter"></i>
          </button>

          <Modal
            isOpen={this.state.showRefineModal}
            onRequestClose={this.closeRefineModal}
            style={ModalStyles}
          >
            <div className="modal-header">
              <a className="modal-close" onClick={this.closeRefineModal}>
                <span className="icon icon-close-l"></span>
              </a>
            </div>

            <div className="modal-body">
              { this.renderOptions() }
            </div>
          </Modal>
        </MediaQuery>
      </div>
    )
  }
}

